import { useQuery } from '@apollo/client/react';
import { FilterList as FilterListIcon, Search } from '@mui/icons-material';
import RestoreIcon from '@mui/icons-material/Restore';
import { Grid, TextField, InputAdornment, IconButton, Tooltip, Button } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { titleize } from 'inflection';
import { find } from 'lodash';
import { debounce, map as mapFp } from 'lodash/fp';
import React, { useContext, useRef, useState } from 'react';

import FilterAutocomplete from '../FilterAutocomplete';
import { type TasksFiltersType } from '../ManagerComponents/ManagerTabs/TasksTab';

import CurrentUserContext from '@/Contexts/CurrentUser';
import {
  GetPreloadTasksDrawerFiltersDataDocument,
  type Event,
  type Report,
  type Project,
  TaskPriorityEnum,
  TaskStatusEnum,
} from '@/graphql/generated/graphql';
import { ENTITY_TYPE } from '@/utils/constants';

interface DrawerFiltersProps {
  filters: TasksFiltersType;
  onChange: (key: keyof TasksFiltersType, value: TasksFiltersType[keyof TasksFiltersType]) => void;
  onReset: () => void;
  disableResetButton: boolean;
}

const normalDebounce = debounce(500);
const mapNode = mapFp('node');

const DrawerFilters: React.FC<DrawerFiltersProps> = ({ filters, onChange, onReset, disableResetButton }) => {
  const { currentCompany, dateTimePreference } = useContext(CurrentUserContext);
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [showFilters, setShowFilters] = useState(false);

  const { data: preloadData } = useQuery(GetPreloadTasksDrawerFiltersDataDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !currentCompany?.id,
    variables: { companyId: currentCompany?.id as string },
  });

  const getEntityOptions = () => {
    if (filters.entity.type === 'Project') {
      return mapNode(preloadData?.getProjects?.edges);
    }
    if (filters.entity.type === 'Report') {
      return mapNode(preloadData?.getCompanyReports?.edges);
    }
    if (filters.entity.type === 'Event') {
      return preloadData?.getCompanyEvents || [];
    }

    return [];
  };

  const getEntityOptionLabel = (option: Project | Report | Event) => {
    if (option.__typename === 'Report') {
      return `${option.project.code} - ${dayjs(option.reportDate).format(dateTimePreference.date)}`;
    }

    if (option.__typename === 'Project' || option.__typename === 'Event') {
      return option.title;
    }

    return '';
  };

  return (
    <Grid
      container
      sx={{ px: 2, py: 1 }}
      justifyContent="space-between"
      data-testid="note-filters"
      alignContent="flex-start"
      maxHeight="fit-content"
      flexDirection="row"
      size={12}
    >
      <Grid size={10}>
        <TextField
          size="small"
          placeholder="Search"
          fullWidth
          defaultValue={filters.searchText}
          onChange={(e) => {
            debounceFunc.cancel();
            debounceFunc(() => {
              onChange('searchText', e.target.value);
            });
          }}
          slotProps={{
            input: {
              sx: { fontSize: 14 },
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>
      <Grid size={1}>
        <Tooltip title="Toggle Filters">
          <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </Grid>
      {showFilters && (
        <Grid container size={12} sx={{ mt: 1 }} spacing={2}>
          <FilterAutocomplete
            textFieldLabel="Created By"
            options={mapNode(preloadData?.getEmployees?.edges) || []}
            onChange={(_, value) => {
              onChange('createdById', value ? value.companyEmployment.id : '');
            }}
            getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
            value={
              find(
                mapNode(preloadData?.getEmployees?.edges) || [],
                (employee) => employee.id === filters.createdById,
              ) || ''
            }
          />
          <FilterAutocomplete
            textFieldLabel="Assigned To"
            options={mapNode(preloadData?.getEmployees?.edges) || []}
            onChange={(_, value) => {
              onChange('assigneeId', value ? value.companyEmployment.id : '');
            }}
            getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
            value={
              find(mapNode(preloadData?.getEmployees?.edges) || [], (employee) => employee.id === filters.assigneeId) ||
              ''
            }
          />
          <FilterAutocomplete
            textFieldLabel="Status"
            options={Object.values(TaskStatusEnum)}
            onChange={(_, value) => {
              onChange('status', value);
            }}
            getOptionLabel={(option) => titleize(option)}
          />
          <FilterAutocomplete
            textFieldLabel="Priority"
            options={Object.values(TaskPriorityEnum)}
            onChange={(_, value) => {
              onChange('priority', value);
            }}
            getOptionLabel={(option) => titleize(option)}
          />
          <FilterAutocomplete
            textFieldLabel="Entity Type"
            options={ENTITY_TYPE}
            onChange={(_, value) => {
              onChange('entity', { type: value, id: '' });
            }}
          />
          {filters.entity.type && (
            <Grid size={12}>
              <FilterAutocomplete
                textFieldLabel={`Select ${filters.entity.type}`}
                options={getEntityOptions() || []}
                value={find(getEntityOptions(), (ent) => ent.id == filters.entity.id) || ''}
                getOptionLabel={(option) => (option ? getEntityOptionLabel(option) : '')}
                onChange={(_, value) => {
                  onChange('entity', { type: filters.entity.type, id: value.id });
                }}
              />
            </Grid>
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date From"
              value={filters.dueDateFrom ? dayjs(filters.dueDateFrom) : null}
              onChange={(date) => onChange('dueDateFrom', date)}
              format={dateTimePreference.date}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                },
              }}
            />
            <DatePicker
              label="Due Date To"
              value={filters.dueDateTo ? dayjs(filters.dueDateTo) : null}
              onChange={(date) => onChange('dueDateTo', date)}
              format={dateTimePreference.date}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                },
              }}
            />
          </LocalizationProvider>
          <Grid size={12} container alignItems="center" justifyContent="flex-start">
            <Button
              variant="outlined"
              onClick={onReset}
              fullWidth
              startIcon={<RestoreIcon />}
              size="small"
              disabled={disableResetButton}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default DrawerFilters;
